import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { Send, X, Maximize2, Maximize, Check, Trash2, PanelLeftClose, PanelRightClose, } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { ChatMessage } from './ChatMessage';
import { ProductCarousel } from './ProductCarousel';
import { TypingIndicator } from './TypingIndicator';
import { PlaybookStep } from './PlaybookStep';
import { TopicsBar } from './TopicsBar';
import { useChat } from '../../contexts/ChatContext';
import { wac } from '../utils/wacClassNames';
import cartService from '../../services/cartService';
import { updateCartCountInHeader } from '../../utils/cart';
import { useTranslation } from '../../hooks/useTranslation';
// Get logo path from WordPress localized script
const getLogoUrl = () => {
    const wooAI = window.wooAiChatbot || window.wooAIChatbot;
    return wooAI?.pluginUrl ? `${wooAI.pluginUrl}/assets/images/ai-assistant-figma.png` : '';
};
// Get launcher style from appearance config
const getLauncherStyle = () => {
    const config = window.wooAIChatConfig;
    return (config?.appearance?.launcherStyle || 'pill');
};
// Get widget position from config — determines if widget sits on left or right
const getWidgetSide = (isRTL) => {
    const config = window.wooAIChatConfig;
    const pos = config?.widgetPosition;
    if (pos === 'bottom-left')
        return 'left';
    if (pos === 'bottom-right')
        return 'right';
    // Default: RTL sites → left, LTR sites → right
    return isRTL ? 'left' : 'right';
};
// Get browser language code (e.g., 'en', 'uk', 'he')
const getBrowserLanguage = () => {
    const lang = navigator.language || navigator.userLanguage || 'en';
    return lang.split('-')[0].toLowerCase(); // 'en-US' -> 'en'
};
// Get bot name from appearance config
const getBotName = () => {
    const config = window.wooAIChatConfig;
    return config?.appearance?.botName || 'AI Assistant';
};
// Get bot avatar from appearance config (emoji or image URL)
const getBotAvatar = () => {
    const config = window.wooAIChatConfig;
    return config?.appearance?.botAvatar || '🤖';
};
// Returns true if the avatar URL is a PNG or WebP — formats that support transparency.
// WP Media Library URLs can have query params like ?ver=1.2.3, so we check for the
// extension before any query string separator as well.
const isTransparentAvatarFormat = (url) => {
    if (!url)
        return false;
    const lower = url.toLowerCase();
    return (lower.endsWith('.png') ||
        lower.endsWith('.webp') ||
        lower.includes('.png?') ||
        lower.includes('.webp?') ||
        lower.includes('.png&') ||
        lower.includes('.webp&'));
};
// Get primary color for pulse animation
const getPulseColor = () => {
    const config = window.wooAIChatConfig;
    return config?.appearance?.primaryColor || '#6366F1';
};
// Convert hex to rgba for pulse animation
const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};
// Generate pulse box-shadow keyframes using primary color
const getPulseShadows = () => {
    const color = getPulseColor();
    const baseShadow = `0px 8px 24px 0px ${hexToRgba(color, 0.35)}`;
    return [
        `${baseShadow}, 0 0 0 0px ${hexToRgba(color, 0.5)}`,
        `${baseShadow}, 0 0 0 20px ${hexToRgba(color, 0)}`,
        `${baseShadow}, 0 0 0 0px ${hexToRgba(color, 0)}`,
    ];
};
// Get welcome message based on browser language
const getWelcomeMessage = () => {
    const wooAI = window.wooAiChatbot || window.wooAIChatbot;
    const config = window.wooAIChatConfig;
    // Try to get language-specific welcome message
    const browserLang = getBrowserLanguage();
    const welcomeMessages = wooAI?.welcomeMessages || config?.welcomeMessages || {};
    // Check for exact match first, then fallback
    if (welcomeMessages[browserLang]) {
        return welcomeMessages[browserLang];
    }
    // Fallback to default welcome message
    return wooAI?.welcomeMessage || config?.initialMessage || 'Hi! How can I help you today?';
};
export function ChatWidgetBottom({ initialState = 'icon', initialConversation = 'conversation', }) {
    const { t, isRTL, direction } = useTranslation();
    const dir = direction;
    const side = getWidgetSide(isRTL);
    const { messages: contextMessages, sessionId, sendMessage, loading, hasMoreHistory, isLoadingHistory, loadMoreHistory, clearHistory, } = useChat();
    // Full-bar launcher starts in 'collapsed' state (wide bar with input + suggestions)
    const isFullBar = getLauncherStyle() === 'full-bar';
    const effectiveInitial = isFullBar ? 'collapsed' : initialState;
    const [widgetState, setWidgetState] = useState(effectiveInitial);
    // Helper: minimize action goes to 'collapsed' in full-bar mode, 'icon' otherwise
    const minimizeWidget = useCallback(() => {
        setWidgetState(isFullBar ? 'collapsed' : 'icon');
    }, [isFullBar]);
    const handleNewChat = async () => {
        try {
            await clearHistory();
        }
        catch (err) {
            // Even if server-side session clear fails (e.g. 403 for logged-in users),
            // still reset the UI so the user can start fresh.
            console.warn('[WooAI] clearHistory failed, resetting UI anyway:', err);
        }
        setConversationState('empty');
    };
    const [conversationState, setConversationState] = useState(contextMessages.length > 0 ? 'conversation' : initialConversation);
    const [input, setInput] = useState('');
    const [dockInputValue, setDockInputValue] = useState('');
    const [pendingImage, setPendingImage] = useState(null);
    const [dragOver, setDragOver] = useState(false);
    const imageInputRef = useRef(null);
    const [cartNotification, setCartNotification] = useState({ show: false });
    const [escalationDismissed, setEscalationDismissed] = useState(() => {
        try {
            return !!sessionStorage.getItem('waa-whatsapp-escalation-dismissed');
        }
        catch {
            return false;
        }
    });
    // Variant 2: rotating placeholder — use ref to avoid re-renders that break Playwright stability
    const chatInputRef = useRef(null);
    const placeholderIndexRef = useRef(0);
    // Variant 6: pulsing animation on image icon
    const [imagePulsing, setImagePulsing] = useState(() => {
        try {
            return !sessionStorage.getItem('waa-image-pulse-shown');
        }
        catch {
            return false;
        }
    });
    // Refs for auto-scroll functionality (Bug #1)
    const messagesContainerRef = useRef(null);
    const messagesEndRef = useRef(null);
    const [userScrolledUp, setUserScrolledUp] = useState(false);
    const [vpRect, setVpRect] = useState(null);
    // True only on a real mouse desktop (fine pointer + wide window). Every iPad
    // — any size, any orientation — is a coarse-pointer touch device and must
    // FILL the viewport, not get the 844px desktop card. The old `vpRect.w>=1024`
    // test wrongly matched iPad Pro 12.9"/13" portrait (w=1024) and ALL iPads in
    // landscape (w>=1024) → half-screen panel (#366). iPhone is coarse → fill,
    // identical to before (its approved layout never went through the card path).
    const [isDesktopCard, setIsDesktopCard] = useState(false);
    const widgetJustOpenedRef = useRef(false);
    // iOS Safari/Chrome frequently omit the final visualViewport `resize` after the
    // soft keyboard dismisses (e.g. on send), leaving the panel sized to the
    // keyboard-open viewport. handleSend / blur call this to re-read the settled
    // viewport at several delays so the panel snaps back to full height.
    const scheduleViewportResyncRef = useRef(() => { });
    const messages = useMemo(() => contextMessages.map((msg) => ({
        id: msg.id,
        type: msg.role === 'user' ? 'user' : msg.role === 'assistant' ? 'bot' : 'system',
        content: msg.content,
        timestamp: new Date(msg.timestamp).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        }),
        image: msg.image,
        products: msg.products,
        tool_results: msg.tool_results,
        cart_actions: msg.cart_actions,
        cart: msg.cart,
    })), [contextMessages]);
    useEffect(() => {
        if ((messages.length > 0 || loading) && conversationState === 'empty') {
            setConversationState('conversation');
        }
    }, [messages.length, loading, conversationState]);
    // WhatsApp handoff config from server
    const whatsappConfig = window.wooAIChatConfig?.whatsapp || { enabled: false };
    // Sidebar config
    const sidebarConfig = window.wooAIChatConfig?.sidebar ?? {
        enabled: true,
        side: 'right',
        width: 30,
    };
    // Lock body scroll when chat is open on mobile to prevent background scrolling
    useEffect(() => {
        const isOpen = widgetState === 'expanded' || widgetState === 'minimized' || widgetState === 'sidebar';
        const isMobile = window.innerWidth < 768;
        if (isOpen && isMobile) {
            const scrollY = window.scrollY;
            document.body.style.overflow = 'hidden';
            document.body.style.position = 'fixed';
            document.body.style.top = `-${scrollY}px`;
            // Span the viewport via left/right, NOT width:100%. The Donkey theme
            // already sets body{padding-right:15px}; width:100% + that padding makes
            // the fixed body wider than the viewport, which iOS Safari "fixes" by
            // zooming/shifting the page — and the broken state persists after the
            // lock is removed (#363, site layout broken after closing the chat).
            document.body.style.left = '0';
            document.body.style.right = '0';
            return () => {
                // Dismiss the iOS keyboard BEFORE unlocking the body. If an input is
                // still focused (keyboard up) when position:fixed is removed, iOS
                // re-scrolls/zooms to the field and the page stays shifted after the
                // chat closes — the keyboard-only half of #363 (plain open+scroll+
                // close already returned cleanly; only the keyboard path broke).
                const active = document.activeElement;
                if (active && typeof active.blur === 'function')
                    active.blur();
                document.body.style.overflow = '';
                document.body.style.position = '';
                document.body.style.top = '';
                document.body.style.left = '';
                document.body.style.right = '';
                // The keyboard dismiss animates over ~300ms and iOS keeps overriding
                // scroll while it runs, so one rAF is too early. Re-assert the saved
                // position across the settle window. Idempotent for the no-keyboard
                // case (same scrollY, page already there) so the approved plain
                // open+scroll+close path is unchanged.
                const restore = () => window.scrollTo(0, scrollY);
                requestAnimationFrame(restore);
                [60, 200, 350].forEach((d) => window.setTimeout(restore, d));
            };
        }
    }, [widgetState]);
    // Scroll to bottom helper - instant for initial load, smooth for new messages
    const scrollToBottom = useCallback((instant = false) => {
        const container = messagesContainerRef.current;
        if (!container)
            return;
        requestAnimationFrame(() => {
            container.scrollTop = container.scrollHeight;
            if (!instant && messagesEndRef.current) {
                messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
            }
        });
    }, []);
    // Auto-scroll to bottom when new messages arrive (Bug #1)
    useEffect(() => {
        // Only auto-scroll if user hasn't manually scrolled up
        if (!userScrolledUp) {
            // Use instant scroll when widget just opened (avoids fighting with animation)
            const isInstant = widgetJustOpenedRef.current;
            widgetJustOpenedRef.current = false;
            scrollToBottom(isInstant);
        }
    }, [messages, loading, userScrolledUp, scrollToBottom]);
    // Auto-scroll to bottom when widget expands or enters sidebar
    useEffect(() => {
        if (widgetState === 'expanded' || widgetState === 'sidebar') {
            setUserScrolledUp(false);
            widgetJustOpenedRef.current = true;
            // Use rAF to ensure DOM is painted before scrolling
            const scrollAfterPaint = () => {
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        scrollToBottom(true);
                        // Additional delayed scroll for messages that load async
                        setTimeout(() => {
                            if (!userScrolledUp) {
                                scrollToBottom(true);
                            }
                        }, 500);
                    });
                });
            };
            // Initial scroll after animation starts
            setTimeout(scrollAfterPaint, 300);
        }
    }, [widgetState, scrollToBottom]); // eslint-disable-line react-hooks/exhaustive-deps
    // Variant 2: rotate placeholder every 3 seconds when input is empty
    // Uses direct DOM mutation (ref) to avoid React re-renders which break Playwright stability checks.
    useEffect(() => {
        const placeholders = [t('typePlaceholder'), t('sendPhotoPlaceholder'), t('findByImage')];
        if (input)
            return; // don't rotate while typing
        const interval = setInterval(() => {
            placeholderIndexRef.current = (placeholderIndexRef.current + 1) % placeholders.length;
            if (chatInputRef.current && !chatInputRef.current.value) {
                chatInputRef.current.placeholder = placeholders[placeholderIndexRef.current];
            }
        }, 3000);
        return () => clearInterval(interval);
    }, [input, t]);
    // Variant 6: stop pulsing after 10 seconds
    useEffect(() => {
        if (!imagePulsing)
            return;
        const timer = setTimeout(() => {
            setImagePulsing(false);
            try {
                sessionStorage.setItem('waa-image-pulse-shown', '1');
            }
            catch { }
        }, 10000);
        return () => clearTimeout(timer);
    }, [imagePulsing]);
    // Global Donkey theme cart drawer refresh — listens for WC cart events fired by the
    // theme's own add-to-cart handlers (quick-view modal, product page form, etc.).
    // Feature-gated: runs only when donkey_cart_params + updateFragments are present.
    // Debounced at 1 s to avoid double-refresh when chat-initiated adds also fire the event.
    useEffect(() => {
        const jq = window.jQuery;
        if (typeof jq === 'undefined')
            return;
        let lastRefreshTime = 0;
        const refreshDonkeyCartGlobal = async () => {
            const now = Date.now();
            if (now - lastRefreshTime < 1000)
                return; // debounce 1 second
            lastRefreshTime = now;
            const params = window.donkey_cart_params;
            const updateFn = window.updateFragments;
            if (!params?.ajax_url || typeof updateFn !== 'function')
                return;
            try {
                const res = await fetch(params.ajax_url, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body: 'action=get_refreshed_fragments',
                    credentials: 'same-origin',
                });
                const data = await res.json();
                if (data?.success && data.data?.fragments) {
                    updateFn(data.data.fragments);
                    console.log('[WooAI] Global cart refresh: Donkey drawer updated via added_to_cart event');
                }
            }
            catch (e) {
                console.warn('[WooAI] Global Donkey cart refresh failed:', e);
            }
        };
        // Listen for ANY cart mutation event from WC or the theme.
        // Donkey theme fires 'added_to_cart' after custom_add_to_cart AJAX completes.
        const events = 'added_to_cart removed_from_cart updated_cart_totals wc_cart_emptied';
        jq(document.body).on(events, () => {
            // Small delay to let the WC session commit before reading fragments
            setTimeout(refreshDonkeyCartGlobal, 500);
        });
        return () => {
            jq(document.body).off(events);
        };
    }, []);
    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia)
            return;
        const mq = window.matchMedia('(min-width: 1024px) and (pointer: fine)');
        const apply = () => setIsDesktopCard(mq.matches);
        apply();
        mq.addEventListener('change', apply);
        return () => mq.removeEventListener('change', apply);
    }, []);
    useEffect(() => {
        if (typeof window === 'undefined' || !window.visualViewport)
            return;
        const root = document.documentElement;
        const update = () => {
            const vv = window.visualViewport;
            // Ignore absurd transient values iOS can momentarily report mid-animation.
            if (vv && vv.height > 120) {
                root.style.setProperty('--waa-vh', `${vv.height}px`);
                setVpRect({
                    h: vv.height,
                    top: vv.offsetTop,
                    w: vv.width,
                    left: vv.offsetLeft,
                });
            }
        };
        // iOS often does not fire a final `resize` once the keyboard finishes
        // dismissing — re-read at several delays to catch the settled viewport.
        let resyncTimers = [];
        const scheduleResync = () => {
            resyncTimers.forEach((t) => clearTimeout(t));
            resyncTimers = [60, 200, 450, 800].map((d) => window.setTimeout(update, d));
        };
        scheduleViewportResyncRef.current = scheduleResync;
        update();
        const vv = window.visualViewport;
        vv.addEventListener('resize', update);
        vv.addEventListener('scroll', update);
        // Keyboard show/hide is signalled by focus changes on the input; window
        // resize / orientation / pageshow cover Safari chrome (URL bar) collapse.
        const onFocusOut = () => scheduleResync();
        window.addEventListener('resize', update);
        window.addEventListener('orientationchange', scheduleResync);
        window.addEventListener('pageshow', update);
        document.addEventListener('focusin', update, true);
        document.addEventListener('focusout', onFocusOut, true);
        return () => {
            resyncTimers.forEach((t) => clearTimeout(t));
            scheduleViewportResyncRef.current = () => { };
            vv.removeEventListener('resize', update);
            vv.removeEventListener('scroll', update);
            window.removeEventListener('resize', update);
            window.removeEventListener('orientationchange', scheduleResync);
            window.removeEventListener('pageshow', update);
            document.removeEventListener('focusin', update, true);
            document.removeEventListener('focusout', onFocusOut, true);
        };
    }, []);
    // Detect if user manually scrolled up
    const handleScroll = useCallback(() => {
        const container = messagesContainerRef.current;
        if (!container)
            return;
        const isAtBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 50;
        setUserScrolledUp(!isAtBottom);
    }, []);
    const products = [
        {
            id: '1',
            name: 'Premium Wireless Headphones',
            price: 199.99,
            salePrice: 149.99,
            rating: 4.8,
            reviews: 256,
            image: 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=400&h=400&fit=crop',
            attributes: ['Bluetooth 5.0', 'Noise Canceling', '40h Battery'],
            inStock: true,
        },
        {
            id: '2',
            name: 'Running Shoes Pro',
            price: 129.99,
            salePrice: 99.99,
            rating: 4.6,
            reviews: 128,
            image: 'https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=400&h=400&fit=crop',
            attributes: ['Size 9-12', 'Lightweight', 'Breathable'],
            inStock: true,
        },
        {
            id: '3',
            name: 'Smartwatch Series 5',
            price: 299.99,
            rating: 4.9,
            reviews: 412,
            image: 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=400&h=400&fit=crop',
            attributes: ['Heart Rate', 'GPS', 'Waterproof'],
            inStock: true,
        },
    ];
    const handleImageSelect = (e) => {
        const file = e.target.files?.[0];
        if (file)
            processImageFile(file);
        e.target.value = '';
    };
    /**
     * Process an image File (shared by file input, paste, and drag-drop).
     */
    const processImageFile = (file) => {
        if (!file.type.match(/^image\/(jpeg|png|webp|gif|bmp)$/))
            return;
        if (file.size > 10 * 1024 * 1024)
            return;
        const reader = new FileReader();
        reader.onload = (ev) => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                const maxSize = 1024;
                let { width, height } = img;
                if (width > maxSize || height > maxSize) {
                    if (width > height) {
                        height = (height / width) * maxSize;
                        width = maxSize;
                    }
                    else {
                        width = (width / height) * maxSize;
                        height = maxSize;
                    }
                }
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx?.drawImage(img, 0, 0, width, height);
                const compressed = canvas.toDataURL('image/jpeg', 0.8);
                const base64Only = compressed.replace(/^data:image\/\w+;base64,/, '');
                setPendingImage({ data: base64Only, preview: compressed });
            };
            img.src = ev.target?.result;
        };
        reader.readAsDataURL(file);
    };
    /**
     * Handle paste from clipboard (Ctrl+V with image).
     */
    const handlePaste = (e) => {
        const items = e.clipboardData?.items;
        if (!items)
            return;
        for (let i = 0; i < items.length; i++) {
            if (items[i].type.startsWith('image/')) {
                e.preventDefault();
                const file = items[i].getAsFile();
                if (file)
                    processImageFile(file);
                return;
            }
        }
    };
    /**
     * Handle drag-and-drop image into chat.
     */
    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(false);
        const file = e.dataTransfer?.files?.[0];
        if (file && file.type.startsWith('image/')) {
            processImageFile(file);
        }
    };
    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(true);
    };
    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(false);
    };
    const handleSend = async (overrideText) => {
        const messageText = (overrideText ?? input).trim();
        if (!messageText || loading) {
            return;
        }
        setInput('');
        setWidgetState((prev) => (prev === 'sidebar' ? 'sidebar' : 'expanded'));
        setConversationState('conversation');
        // Tapping the send button blurs the input → keyboard dismisses, but iOS may
        // not fire a final visualViewport resize. Re-read the settled viewport so the
        // panel snaps back to full height instead of staying keyboard-cropped (#350).
        scheduleViewportResyncRef.current();
        const imageToSend = pendingImage?.data;
        setPendingImage(null);
        try {
            await sendMessage(messageText, {
                currentPage: typeof window !== 'undefined' ? window.location.pathname : 'N/A',
                pageContext: window.wooAIChatConfig?.pageContext || undefined,
                ...(imageToSend ? { image: imageToSend } : {}),
            });
        }
        catch (err) {
            console.error('Failed to send message:', err);
        }
    };
    const handleQuestionClick = (question) => {
        handleSend(question);
    };
    const handleViewProduct = (_productId) => {
        // Placeholder for product detail navigation
    };
    const [_addingToCart, setAddingToCart] = useState(null);
    const handleAddToCart = useCallback(async (productId, variationId, productName) => {
        try {
            setAddingToCart(productId);
            const numericProductId = typeof productId === 'string' ? parseInt(productId, 10) : productId;
            const response = await cartService.addToCart({
                product_id: numericProductId,
                variation_id: variationId,
                quantity: 1,
            });
            // Use response cart.item_count for accurate header update (not DOM increment)
            const newCount = response.cart?.item_count;
            if (newCount !== undefined) {
                const selectors = [
                    '.cart-contents .count',
                    '.cart-contents-count',
                    '.header__action-count',
                    '#products-count',
                    '.mini-cart-count',
                    '.cart-count',
                    '.woocommerce-cart-count',
                    '.site-header-cart .count',
                ];
                const updated = new Set();
                selectors.forEach((sel) => {
                    document.querySelectorAll(sel).forEach((el) => {
                        if (!updated.has(el)) {
                            updated.add(el);
                            el.textContent = String(newCount);
                        }
                    });
                });
            }
            // Call shared utility to update all cart count selectors (incl. theme-specific ones)
            // and dispatch WC Blocks custom event
            await updateCartCountInHeader();
            // Robust mini-cart refresh — identical to the chat-message path
            // (executeCartActionsImmediate). Firing only `wc_fragment_refresh` was
            // insufficient on Donkey, whose custom drawer isn't a WC fragment and
            // whose theme drops WC's cart-fragments.js listener, so the cart stayed
            // stale until a manual page reload (#362). Directly fetch + apply.
            try {
                const $ = window.jQuery;
                if ($) {
                    $(document.body).trigger('added_to_cart', [response, null, null]);
                }
                // Let the WC session write propagate before reading fragments.
                await new Promise((resolve) => setTimeout(resolve, 300));
                const fragResp = await fetch('/?wc-ajax=get_refreshed_fragments', {
                    method: 'POST',
                    credentials: 'same-origin',
                });
                const fragData = await fragResp.json().catch(() => null);
                if (fragData?.fragments) {
                    const updateFn = window.updateFragments;
                    if (typeof updateFn === 'function')
                        updateFn(fragData.fragments);
                    if ($) {
                        $.each(fragData.fragments, (key, value) => $(key).replaceWith(value));
                        $(document.body).trigger('wc_fragment_refresh');
                        $(document.body).trigger('wc_fragments_refresh');
                        $(document.body).trigger('wc_fragments_refreshed');
                        $(document.body).trigger('updated_wc_div');
                    }
                }
            }
            catch {
                /* jQuery / fragments endpoint not available */
            }
            // Donkey theme's custom cart drawer is not a WC fragment — refresh it
            // via the theme's own admin-ajax action (feature-gated, no-ops elsewhere).
            try {
                const donkeyParams = window.donkey_cart_params;
                const updateFragmentsFn = window.updateFragments;
                if (donkeyParams?.ajax_url && typeof updateFragmentsFn === 'function') {
                    const donkeyRes = await fetch(donkeyParams.ajax_url, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                        body: 'action=get_refreshed_fragments',
                        credentials: 'same-origin',
                    });
                    const donkeyData = await donkeyRes.json();
                    if (donkeyData?.success && donkeyData.data?.fragments) {
                        updateFragmentsFn(donkeyData.data.fragments);
                    }
                }
            }
            catch {
                /* Donkey theme globals not present */
            }
            // Donkey theme: open the cart modal so user sees added item immediately.
            // Theme exposes window.openModal at themes/donkey/assets/js/main.js:154
            setTimeout(() => {
                const cartModal = document.querySelector('[data-modal="cart"]');
                const win = window;
                if (cartModal && typeof win.openModal === 'function') {
                    win.openModal(cartModal);
                }
            }, 250);
            // Show success notification
            setCartNotification({ show: true, productName: productName || t('product') });
            // Auto-hide notification after 3 seconds
            setTimeout(() => {
                setCartNotification({ show: false });
            }, 3000);
        }
        catch (error) {
            console.error('Failed to add to cart:', error);
        }
        finally {
            setAddingToCart(null);
        }
    }, [t]);
    const introTimestamp = useMemo(() => new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }), []);
    const sharedInputStyles = {
        borderColor: 'var(--waa-gray-300)',
        '--tw-ring-color': 'var(--waa-primary)',
        // iOS Safari auto-zooms the whole page when a focused input's font-size
        // is < 16px, and the zoom is NOT reliably reset on blur/close — the page
        // stays magnified after the chat closes (#363, the keyboard-open path).
        // 16px is the documented threshold that suppresses the auto-zoom; every
        // focusable text input below keeps this floor (per-input overrides must
        // not drop under 16px).
        fontSize: '16px',
    };
    if (widgetState === 'icon') {
        const launcherStyle = getLauncherStyle();
        if (launcherStyle === 'circle-icon') {
            return (_jsx(motion.button, { type: "button", onClick: () => setWidgetState('expanded'), initial: { scale: 0, opacity: 0 }, animate: {
                    scale: [1, 1.1, 1],
                    opacity: 1,
                    boxShadow: getPulseShadows(),
                }, transition: {
                    scale: { duration: 2, repeat: Infinity, repeatDelay: 2, ease: 'easeInOut' },
                    boxShadow: { duration: 2, repeat: Infinity, repeatDelay: 2, ease: 'easeOut' },
                    opacity: { duration: 0.3 },
                }, exit: { scale: 0, opacity: 0 }, whileHover: { scale: 1.1 }, whileTap: { scale: 0.95 }, style: {
                    position: 'fixed',
                    bottom: '24px',
                    ...(side === 'left'
                        ? { left: '24px', right: 'auto' }
                        : { right: '24px', left: 'auto' }),
                    width: '60px',
                    height: '60px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%',
                    background: 'var(--waa-primary, #6366F1)',
                    boxShadow: 'var(--waa-launcher-shadow, 0px 8px 32px 0px rgba(99, 102, 241, 0.4))',
                    border: 'none',
                    cursor: 'pointer',
                    zIndex: 2147483647,
                    padding: 0,
                }, "aria-label": "Open chat", children: _jsx("div", { style: {
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        background: '#ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                    }, children: _jsx("img", { src: getBotAvatar().startsWith('http') ? getBotAvatar() : getLogoUrl(), alt: getBotName(), style: {
                            width: '100%',
                            height: '100%',
                            objectFit: getBotAvatar().startsWith('http') ? 'cover' : 'contain',
                        } }) }) }));
        }
        if (launcherStyle === 'circle-text') {
            return (_jsx(motion.button, { type: "button", onClick: () => setWidgetState('expanded'), initial: { scale: 0, opacity: 0 }, animate: {
                    scale: [1, 1.1, 1],
                    opacity: 1,
                    boxShadow: getPulseShadows(),
                }, transition: {
                    scale: { duration: 2, repeat: Infinity, repeatDelay: 2, ease: 'easeInOut' },
                    boxShadow: { duration: 2, repeat: Infinity, repeatDelay: 2, ease: 'easeOut' },
                    opacity: { duration: 0.3 },
                }, exit: { scale: 0, opacity: 0 }, whileHover: { scale: 1.1 }, whileTap: { scale: 0.95 }, style: {
                    position: 'fixed',
                    bottom: '24px',
                    ...(side === 'left'
                        ? { left: '24px', right: 'auto' }
                        : { right: '24px', left: 'auto' }),
                    width: '60px',
                    height: '60px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%',
                    background: 'var(--waa-primary, #6366F1)',
                    boxShadow: 'var(--waa-launcher-shadow, 0px 8px 32px 0px rgba(99, 102, 241, 0.4))',
                    border: 'none',
                    cursor: 'pointer',
                    zIndex: 2147483647,
                    padding: 0,
                }, "aria-label": "Open chat", children: _jsx("span", { style: {
                        color: '#ffffff',
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 700,
                        fontSize: '18px',
                        lineHeight: 1,
                    }, children: "AI" }) }));
        }
        // Dock style: minimalist floating bottom bar with functional input
        if (launcherStyle === 'dock') {
            const dockConfig = window.wooAIChatConfig;
            const accentColor = dockConfig?.appearance?.primaryColor || 'var(--waa-primary, #6366F1)';
            const placeholderText = dockConfig?.appearance?.greetingMessage || t('askAnything') || 'Ask me anything...';
            const handleDockSubmit = () => {
                const text = dockInputValue.trim();
                if (!text || loading)
                    return;
                setDockInputValue('');
                setWidgetState('expanded');
                setConversationState('conversation');
                sendMessage(text, {
                    currentPage: typeof window !== 'undefined' ? window.location.pathname : 'N/A',
                    pageContext: window.wooAIChatConfig?.pageContext || undefined,
                });
            };
            return (_jsx(motion.div, { initial: { y: 80, opacity: 0 }, animate: { y: 0, opacity: 1 }, exit: { y: 80, opacity: 0 }, transition: { duration: 0.35, ease: 'easeOut' }, style: {
                    position: 'fixed',
                    bottom: '16px',
                    right: 0,
                    left: 0,
                    zIndex: 2147483647,
                    display: 'flex',
                    justifyContent: 'center',
                    direction: 'ltr',
                }, children: _jsx("div", { style: {
                        width: '92%',
                        maxWidth: '680px',
                        direction: dir,
                    }, children: _jsxs("div", { style: {
                            background: '#ffffff',
                            borderRadius: '16px',
                            boxShadow: '0 4px 24px rgba(0, 0, 0, 0.12)',
                            padding: '8px 8px 8px 16px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                        }, children: [(() => {
                                const avatarUrl = getBotAvatar().startsWith('http') ? getBotAvatar() : getLogoUrl();
                                const isTransparent = getBotAvatar().startsWith('http') && isTransparentAvatarFormat(getBotAvatar());
                                return (_jsx("div", { style: {
                                        width: '32px',
                                        height: '32px',
                                        borderRadius: isTransparent ? '4px' : '50%',
                                        background: isTransparent ? 'transparent' : accentColor,
                                        flexShrink: 0,
                                        overflow: 'hidden',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }, children: _jsx("img", { src: avatarUrl, alt: getBotName(), style: {
                                            width: '28px',
                                            height: '28px',
                                            objectFit: isTransparent
                                                ? 'contain'
                                                : getBotAvatar().startsWith('http')
                                                    ? 'cover'
                                                    : 'contain',
                                            borderRadius: isTransparent ? '0' : '50%',
                                        } }) }));
                            })(), _jsx("input", { type: "text", value: dockInputValue, onChange: (e) => setDockInputValue(e.target.value), onKeyDown: (e) => {
                                    if (e.key === 'Enter' && !e.shiftKey && !loading) {
                                        e.preventDefault();
                                        handleDockSubmit();
                                    }
                                }, placeholder: placeholderText, style: {
                                    flex: 1,
                                    height: '40px',
                                    background: 'var(--waa-gray-50, #f9fafb)',
                                    border: '1px solid var(--waa-gray-200, #e5e7eb)',
                                    borderRadius: '10px',
                                    paddingLeft: '14px',
                                    paddingRight: '14px',
                                    fontSize: '14px',
                                    fontFamily: 'Inter, sans-serif',
                                    fontWeight: 400,
                                    lineHeight: '20px',
                                    color: '#111827',
                                    outline: 'none',
                                    direction: dir,
                                    textAlign: isRTL ? 'right' : 'left',
                                } }), _jsx("button", { type: "button", onClick: handleDockSubmit, disabled: !dockInputValue.trim() || loading, style: {
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '8px',
                                    background: dockInputValue.trim() && !loading ? accentColor : 'transparent',
                                    border: 'none',
                                    cursor: dockInputValue.trim() && !loading ? 'pointer' : 'default',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                    color: dockInputValue.trim() ? '#ffffff' : 'var(--waa-gray-400, #9ca3af)',
                                    transition: 'background 0.2s, color 0.2s',
                                }, "aria-label": "Send message", children: _jsx(Send, { size: 16 }) }), _jsx("button", { type: "button", onClick: () => setWidgetState('expanded'), style: {
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '8px',
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                    color: 'var(--waa-gray-400, #9ca3af)',
                                    transition: 'color 0.2s',
                                }, "aria-label": "Expand chat", children: _jsx(Maximize2, { size: 16 }) })] }) }) }));
        }
        // Default: pill style
        return (_jsxs(motion.button, { type: "button", onClick: () => setWidgetState('expanded'), initial: { scale: 0, opacity: 0 }, animate: {
                scale: [1, 1.08, 1],
                opacity: 1,
                boxShadow: getPulseShadows(),
            }, transition: {
                scale: { duration: 2, repeat: Infinity, repeatDelay: 2, ease: 'easeInOut' },
                boxShadow: { duration: 2, repeat: Infinity, repeatDelay: 2, ease: 'easeOut' },
                opacity: { duration: 0.3 },
            }, exit: { scale: 0, opacity: 0 }, whileHover: { scale: 1.05 }, whileTap: { scale: 0.95 }, style: {
                position: 'fixed',
                bottom: '24px',
                ...(side === 'left' ? { left: '24px', right: 'auto' } : { right: '24px', left: 'auto' }),
                width: 'auto',
                height: '48px',
                paddingLeft: '14px',
                paddingRight: '18px',
                paddingTop: '0',
                paddingBottom: '0',
                gap: '10px',
                display: 'flex',
                alignItems: 'center',
                borderRadius: '33554400px',
                background: 'var(--waa-primary, #6366F1)',
                boxShadow: 'var(--waa-launcher-shadow, 0px 8px 32px 0px rgba(99, 102, 241, 0.4))',
                border: 'none',
                cursor: 'pointer',
                zIndex: 2147483647,
                direction: dir,
            }, "aria-label": "Open chat", children: [(() => {
                    const avatarUrl = getBotAvatar().startsWith('http') ? getBotAvatar() : getLogoUrl();
                    const isTransparent = getBotAvatar().startsWith('http') && isTransparentAvatarFormat(getBotAvatar());
                    return (_jsx("div", { style: {
                            width: '34px',
                            height: '34px',
                            flexShrink: 0,
                            borderRadius: isTransparent ? '4px' : '50%',
                            background: isTransparent ? 'transparent' : 'rgba(255, 255, 255, 0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                        }, children: _jsx("img", { src: avatarUrl, alt: getBotName(), style: {
                                width: '34px',
                                height: '34px',
                                objectFit: isTransparent ? 'contain' : 'cover',
                                borderRadius: isTransparent ? '0' : '50%',
                            } }) }));
                })(), _jsx("span", { style: {
                        color: '#ffffff',
                        fontFamily: 'inherit',
                        fontWeight: 500,
                        fontSize: '14px',
                        lineHeight: '20px',
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                    }, children: t('askAiAssistant') })] }));
    }
    if (widgetState === 'collapsed') {
        return (_jsx("div", { className: wac('collapsed-container'), style: {
                position: 'fixed',
                bottom: '5px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '100%',
                zIndex: 2147483647,
                direction: dir,
            }, children: _jsx("div", { className: wac('px-4 md:px-8 lg:px-12 w-full'), children: _jsxs(motion.div, { initial: { y: 20, opacity: 0 }, animate: { y: 0, opacity: 1 }, transition: { duration: 0.3, ease: 'easeOut' }, className: wac('rounded-2xl border shadow-2xl transition-shadow duration-300 hover:shadow-3xl'), style: {
                        background: 'linear-gradient(135deg, #ffffff 0%, #f0f4ff 50%, #e8f0fe 100%)',
                        borderColor: 'var(--waa-gray-200)',
                        boxShadow: '0 -4px 24px rgba(0, 0, 0, 0.08), 0 4px 24px rgba(0, 0, 0, 0.08)',
                        padding: '21px',
                    }, children: [_jsx("div", { style: { marginBottom: '12px' }, children: _jsxs("div", { className: wac('relative'), children: [_jsx("div", { className: wac('absolute top-1/2 -translate-y-1/2 z-10 flex items-center justify-center'), style: isRTL
                                            ? { right: '16px', width: '20px', height: '20px' }
                                            : { left: '16px', width: '20px', height: '20px' }, children: _jsxs("svg", { width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: [_jsx("rect", { width: "24", height: "24", rx: "6", fill: "var(--waa-primary, #6366F1)" }), _jsx("path", { d: "M12 8V16M8 12H16", stroke: "white", strokeWidth: "2", strokeLinecap: "round" })] }) }), _jsx("input", { type: "text", value: input, onChange: (e) => setInput(e.target.value), onKeyDown: (e) => e.key === 'Enter' && handleSend(), onPaste: handlePaste, onFocus: () => setWidgetState('expanded'), placeholder: t('askAnything'), className: wac('w-full h-12 border focus:outline-none focus:ring-2 focus:ring-offset-0 text-sm transition-all focus:shadow-lg'), style: {
                                            ...sharedInputStyles,
                                            borderRadius: '10px',
                                            paddingInlineStart: '44px',
                                            paddingInlineEnd: '112px',
                                            direction: dir,
                                        } }), _jsxs("div", { className: wac('absolute top-1/2 -translate-y-1/2 flex items-center'), style: isRTL ? { left: '8px', gap: '4px' } : { right: '8px', gap: '4px' }, children: [_jsx(motion.button, { type: "button", onClick: () => setWidgetState('expanded'), whileHover: { scale: 1.1 }, whileTap: { scale: 0.95 }, className: wac('w-8 h-8 rounded-md flex items-center justify-center transition-all hover:bg-[var(--waa-gray-100)]'), "aria-label": "Expand chat", children: _jsx(Maximize, { className: wac('w-4 h-4'), style: { color: 'var(--waa-gray-600)' } }) }), _jsx(motion.button, { type: "button", onClick: () => handleSend(), disabled: !input.trim() || loading, whileHover: { scale: 1.1, rotate: 5 }, whileTap: { scale: 0.95 }, className: wac('w-8 h-8 rounded-md flex items-center justify-center transition-all disabled:opacity-30 disabled:cursor-not-allowed hover:shadow-md'), style: {
                                                    background: input.trim() && !loading ? 'var(--waa-primary)' : 'transparent',
                                                }, "aria-label": t('sendMessage'), children: _jsx(Send, { className: wac('w-4 h-4'), style: { color: input.trim() && !loading ? 'white' : 'var(--waa-gray-400)' } }) })] })] }) }), _jsx(TopicsBar, { onTopicClick: (_topic, message) => {
                                handleQuestionClick(message);
                            } })] }) }) }));
    }
    if (widgetState === 'minimized') {
        return (_jsxs(motion.div, { initial: { scale: 0.9, opacity: 0, y: 20 }, animate: { scale: 1, opacity: 1, y: 0 }, exit: { scale: 0.9, opacity: 0, y: 20 }, transition: { duration: 0.3, ease: 'easeOut' }, className: wac('bg-white flex flex-col overflow-hidden'), style: {
                position: 'fixed',
                bottom: '20px',
                ...(side === 'left' ? { left: '20px', right: 'auto' } : { right: '20px', left: 'auto' }),
                width: 'min(var(--waa-widget-width, 380px), calc(100vw - 32px))',
                height: 'var(--waa-widget-max-height, 532px)',
                maxHeight: 'calc(100dvh - 40px)',
                borderRadius: '24px',
                boxShadow: '0px 8px 32px 0px rgba(0, 0, 0, 0.15)',
                zIndex: 2147483647,
                direction: dir,
            }, children: [_jsxs("div", { className: wac('flex items-center'), style: {
                        height: '64px',
                        flexShrink: 0,
                        padding: '0 20px',
                        background: 'var(--waa-primary, #6366F1)',
                    }, children: [_jsxs("div", { className: wac('flex items-center'), style: { gap: '12px' }, children: [_jsx("div", { className: wac('rounded-full flex items-center justify-center'), style: {
                                        width: '40px',
                                        height: '40px',
                                        background: 'rgba(255, 255, 255, 0.2)',
                                        overflow: 'hidden',
                                    }, children: getBotAvatar().startsWith('http') ? (_jsx("img", { src: getBotAvatar(), alt: getBotName(), style: {
                                            width: '40px',
                                            height: '40px',
                                            objectFit: 'cover',
                                        } })) : (_jsx("span", { style: { fontSize: '20px', lineHeight: 1 }, children: getBotAvatar() })) }), _jsxs("div", { children: [_jsx("div", { style: {
                                                color: '#ffffff',
                                                fontFamily: 'Inter, sans-serif',
                                                fontSize: '16px',
                                                fontWeight: 500,
                                                lineHeight: '24px',
                                                position: 'relative',
                                                top: '-1px',
                                            }, children: getBotName() }), _jsxs("div", { className: wac('flex items-center'), style: {
                                                fontSize: '12px',
                                                color: 'rgba(255, 255, 255, 0.8)',
                                                lineHeight: '16px',
                                                gap: '4px',
                                            }, children: [_jsx("span", { style: {
                                                        width: '8px',
                                                        height: '8px',
                                                        borderRadius: '50%',
                                                        background: '#05df72',
                                                        display: 'block',
                                                    } }), t('online')] })] })] }), _jsxs("div", { className: wac('flex items-center'), style: { gap: '6px', marginInlineStart: 'auto' }, children: [whatsappConfig.enabled && whatsappConfig.phone && (_jsx(motion.button, { type: "button", onClick: () => {
                                        const url = buildWhatsAppUrl(whatsappConfig.phone, messages, whatsappConfig.includeContext);
                                        window.open(url, '_blank');
                                    }, style: {
                                        background: 'rgba(255, 255, 255, 0.15)',
                                        border: 'none',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '32px',
                                        height: '32px',
                                        borderRadius: '10px',
                                        padding: 0,
                                    }, whileHover: { background: 'rgba(255, 255, 255, 0.25)' }, whileTap: { scale: 0.95 }, title: t('whatsappTooltip'), "aria-label": "WhatsApp", children: _jsxs("svg", { viewBox: "0 0 24 24", width: "21", height: "21", fill: "white", xmlns: "http://www.w3.org/2000/svg", style: { width: '21px', height: '21px', color: '#ffffff' }, children: [_jsx("path", { d: "M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" }), _jsx("path", { d: "M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.832-1.438A9.96 9.96 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2zm0 18a8 8 0 01-4.29-1.24l-.31-.18-2.87.85.78-2.83-.21-.33A8 8 0 1112 20z" })] }) })), _jsx(motion.button, { type: "button", onClick: handleNewChat, whileHover: { background: 'rgba(255, 255, 255, 0.25)' }, whileTap: { scale: 0.95 }, style: {
                                        background: 'rgba(255, 255, 255, 0.15)',
                                        border: 'none',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '32px',
                                        height: '32px',
                                        borderRadius: '10px',
                                        padding: 0,
                                    }, "aria-label": t('clearChat'), title: t('clearChat'), children: _jsx(Trash2, { style: { width: '16px', height: '16px', color: '#ffffff' } }) }), _jsx(motion.button, { type: "button", onClick: () => setWidgetState('expanded'), whileHover: { background: 'rgba(255, 255, 255, 0.25)' }, whileTap: { scale: 0.95 }, style: {
                                        background: 'rgba(255, 255, 255, 0.15)',
                                        border: 'none',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '32px',
                                        height: '32px',
                                        borderRadius: '10px',
                                        padding: 0,
                                    }, "aria-label": "Expand", children: _jsx(Maximize2, { style: { width: '16px', height: '16px', color: '#ffffff' } }) }), _jsx(motion.button, { type: "button", onClick: () => minimizeWidget(), whileHover: { background: 'rgba(255, 255, 255, 0.25)' }, whileTap: { scale: 0.95 }, style: {
                                        background: 'rgba(255, 255, 255, 0.15)',
                                        border: 'none',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '32px',
                                        height: '32px',
                                        borderRadius: '10px',
                                        padding: 0,
                                    }, "aria-label": "Close chat", children: _jsx(X, { style: { width: '16px', height: '16px', color: '#ffffff' } }) })] })] }), _jsx("div", { className: wac('flex-1 overflow-y-auto'), style: {
                        flex: '1 1 0%',
                        minHeight: 0,
                        background: 'var(--waa-gray-50)',
                        padding: '20px',
                    } }), _jsxs("div", { className: wac('border-t flex items-center'), style: {
                        height: '56px',
                        flexShrink: 0,
                        padding: '1px 20px 0',
                        borderColor: 'var(--waa-gray-200)',
                        gap: '8px',
                    }, children: [_jsx("input", { type: "text", value: input, onChange: (e) => setInput(e.target.value), onKeyDown: (e) => e.key === 'Enter' && handleSend(), onPaste: handlePaste, onFocus: () => setWidgetState('expanded'), placeholder: t('askAboutProducts'), className: wac('flex-1 border focus:outline-none focus:ring-2 focus:ring-offset-0'), style: {
                                ...sharedInputStyles,
                                height: '40px',
                                padding: '0 12px',
                                borderRadius: '10px',
                                borderColor: 'var(--waa-gray-300)',
                                // Keep >=16px so iOS does not auto-zoom on focus (#363).
                                fontSize: '16px',
                                color: '#0a0a0a',
                                direction: dir,
                            } }), _jsx(motion.button, { type: "button", onClick: () => handleSend(), disabled: !input.trim(), whileTap: { scale: 0.88 }, transition: { duration: 0.1 }, className: wac('rounded-full flex items-center justify-center transition-colors'), style: {
                                width: '40px',
                                height: '40px',
                                background: 'var(--waa-gray-300)',
                                opacity: input.trim() ? 1 : 0.4,
                                cursor: input.trim() ? 'pointer' : 'not-allowed',
                            }, "aria-label": "Send message", children: _jsx(Send, { style: { width: '20px', height: '20px', color: '#ffffff' } }) })] })] }));
    }
    function buildWhatsAppUrl(phone, msgs, includeContext) {
        const cleanPhone = phone.replace(/[^0-9]/g, '');
        if (!includeContext || msgs.length === 0) {
            return `https://wa.me/${cleanPhone}`;
        }
        let text = t('whatsappContextIntro') + '\n\n';
        text += '💬\n';
        msgs.forEach((m) => {
            const isUser = m.type === 'user' || m.role === 'user';
            const prefix = isUser ? '👤' : '🤖';
            const content = m.content || '';
            text += `${prefix} ${content}\n`;
        });
        return `https://wa.me/${cleanPhone}?text=${encodeURIComponent(text)}`;
    }
    // Escalation detection — show banner after N turns if AI repeatedly says it can't help
    function shouldShowEscalation(msgs, threshold) {
        const assistantMsgs = msgs.filter((m) => m.type === 'bot' || m.role === 'assistant');
        if (assistantMsgs.length < threshold)
            return false;
        const noInfoPhrases = [
            'על צערי',
            'לצערי',
            'на жаль',
            'unfortunately',
            'у мене немає',
            'אין לי מידע',
            'не маю інформації',
            "I don't have",
        ];
        const lastThree = assistantMsgs.slice(-3);
        const hasNoInfo = lastThree.filter((m) => noInfoPhrases.some((p) => (m.content || '').toLowerCase().includes(p.toLowerCase()))).length >= 2;
        return hasNoInfo || assistantMsgs.length >= threshold + 3;
    }
    const isSidebar = widgetState === 'sidebar';
    return (_jsxs(_Fragment, { children: [_jsx("style", { children: `
        @keyframes waa-image-pulse {
          0%, 100% { transform: scale(1); filter: drop-shadow(0 0 0px rgba(59, 130, 246, 0.7)); }
          50% { transform: scale(1.15); filter: drop-shadow(0 0 6px rgba(59, 130, 246, 0.8)); }
        }
        .waa-image-pulse {
          animation: waa-image-pulse 1.5s ease-in-out infinite;
        }
      ` }), _jsx(motion.div, { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 }, transition: { duration: 0.2 }, style: {
                    position: 'fixed',
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: 0,
                    zIndex: 9998,
                    background: isSidebar ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.3)',
                }, onClick: () => minimizeWidget() }), _jsx("div", { style: isSidebar
                    ? {
                        position: 'fixed',
                        inset: 0,
                        zIndex: 2147483647,
                        pointerEvents: 'none',
                    }
                    : {
                        position: 'fixed',
                        // Bind the container to the FULL visualViewport rect — width &
                        // offsetLeft too, not just height/top. iOS computes
                        // `left:0;right:0` against the *layout* viewport; with the
                        // Donkey theme's body{padding-right:15px} the page overflows
                        // horizontally, so the panel shifted right and clipped off the
                        // right edge (#350 v0.34.15: huge left gap, right cut off).
                        // Fallbacks (vpRect null pre-mount) exclude Safari chrome.
                        left: vpRect ? vpRect.left : 0,
                        top: vpRect ? vpRect.top : 0,
                        width: vpRect ? vpRect.w : '100vw',
                        height: vpRect ? vpRect.h : '100dvh',
                        zIndex: 2147483647,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'flex-end',
                        pointerEvents: 'none',
                        // Floating card: 12px gap on sides + bottom; the top gap comes
                        // from the panel being (viewport − 24px) tall inside this
                        // flex-end container (#364 — chat must not bleed to screen edges).
                        padding: '0 12px 12px',
                        direction: dir,
                    }, children: _jsxs(motion.div, { initial: isSidebar
                        ? { x: sidebarConfig.side === 'right' ? '100%' : '-100%', opacity: 1 }
                        : { y: '100%', opacity: 0 }, animate: isSidebar ? { x: 0, opacity: 1 } : { y: 0, opacity: 1 }, exit: isSidebar
                        ? { x: sidebarConfig.side === 'right' ? '100%' : '-100%', opacity: 1 }
                        : { y: '100%', opacity: 0 }, transition: { type: 'spring', damping: 25, stiffness: 300 }, id: "wac-widget-panel", ...(!isSidebar && { className: wac('chat-sidebar-responsive') }), style: isSidebar
                        ? {
                            position: 'fixed',
                            top: 0,
                            bottom: 0,
                            [sidebarConfig.side]: 0,
                            width: `${sidebarConfig.width}vw`,
                            minWidth: '320px',
                            maxWidth: '600px',
                            zIndex: 2147483647,
                            display: 'flex',
                            flexDirection: 'column',
                            background: '#ffffff',
                            boxShadow: sidebarConfig.side === 'right'
                                ? '-4px 0 24px rgba(0,0,0,0.12)'
                                : '4px 0 24px rgba(0,0,0,0.12)',
                            pointerEvents: 'auto',
                            overflow: 'hidden',
                            borderRadius: 0,
                        }
                        : {
                            position: 'relative',
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100%',
                            // Floating card sized to the (keyboard-aware) visual viewport.
                            // --waa-vh is JS-updated from window.visualViewport. Driven
                            // here INTENTIONALLY (not via the .chat-sidebar-responsive
                            // class, whose height was dead — the old inline height:'100%'
                            // overrode it for many versions: #350/#352/#364).
                            // Touch devices (every iPhone AND every iPad, any size or
                            // orientation) FILL the viewport minus a 24px margin. The
                            // 844px cap is for true mouse desktops only. Keying off a
                            // coarse/fine pointer media query (not viewport width) is
                            // what fixes #366: width≥1024 wrongly captured iPad Pro
                            // 12.9"/13" portrait and every iPad in landscape, leaving
                            // the panel half-screen. iPhone path is byte-identical to
                            // the Nastya-approved v0.34.16 layout (it never matched the
                            // card branch before either).
                            height: isDesktopCard
                                ? 'min(844px, calc(var(--waa-vh, 100dvh) - 24px))'
                                : 'calc(var(--waa-vh, 100dvh) - 24px)',
                            maxHeight: 'calc(var(--waa-vh, 100dvh) - 24px)',
                            borderRadius: '16px',
                            boxShadow: '0px -4px 24px rgba(0, 0, 0, 0.12), 0px 0px 60px rgba(0, 0, 0, 0.08)',
                            background: '#ffffff',
                            pointerEvents: 'auto',
                            overflow: 'hidden',
                        }, children: [_jsxs("div", { className: wac('flex items-center'), style: {
                                flexShrink: 0,
                                height: '64px',
                                padding: '0 20px',
                                background: 'var(--waa-primary, #6366F1)',
                                borderRadius: isSidebar ? 0 : '16px 16px 0 0',
                            }, children: [_jsxs("div", { className: wac('flex items-center'), style: { gap: '12px' }, children: [(() => {
                                            const avatarVal = getBotAvatar();
                                            const isUrl = avatarVal.startsWith('http');
                                            const isTransparent = isUrl && isTransparentAvatarFormat(avatarVal);
                                            return (_jsx("div", { className: wac('flex items-center justify-center flex-shrink-0'), style: {
                                                    width: '40px',
                                                    height: '40px',
                                                    background: isTransparent ? 'transparent' : 'rgba(255, 255, 255, 0.15)',
                                                    overflow: 'hidden',
                                                    borderRadius: isTransparent ? '4px' : '50%',
                                                }, children: isUrl ? (_jsx("img", { src: avatarVal, alt: getBotName(), style: {
                                                        width: '40px',
                                                        height: '40px',
                                                        objectFit: isTransparent ? 'contain' : 'cover',
                                                        borderRadius: isTransparent ? '0' : '50%',
                                                    } })) : (_jsx("span", { style: { fontSize: '20px', lineHeight: 1 }, children: avatarVal })) }));
                                        })(), _jsxs("div", { children: [_jsx("div", { style: {
                                                        color: '#ffffff',
                                                        fontSize: '16px',
                                                        fontWeight: 500,
                                                        lineHeight: '24px',
                                                    }, children: getBotName() }), _jsxs("div", { className: wac('flex items-center'), style: {
                                                        fontSize: '12px',
                                                        color: 'rgba(255, 255, 255, 0.8)',
                                                        lineHeight: '16px',
                                                        gap: '4px',
                                                    }, children: [_jsx("span", { style: {
                                                                width: '8px',
                                                                height: '8px',
                                                                borderRadius: '50%',
                                                                background: '#05df72',
                                                                display: 'block',
                                                            } }), t('online')] })] })] }), _jsxs("div", { className: wac('flex items-center'), style: { marginInlineStart: 'auto', gap: '6px', flexShrink: 0 }, children: [whatsappConfig.enabled && whatsappConfig.phone && (_jsx(motion.button, { type: "button", onClick: () => {
                                                const url = buildWhatsAppUrl(whatsappConfig.phone, messages, whatsappConfig.includeContext);
                                                window.open(url, '_blank');
                                            }, style: {
                                                background: 'rgba(255, 255, 255, 0.15)',
                                                border: 'none',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: '32px',
                                                height: '32px',
                                                borderRadius: '10px',
                                                padding: 0,
                                            }, whileHover: { background: 'rgba(255, 255, 255, 0.25)' }, whileTap: { scale: 0.95 }, title: t('whatsappTooltip'), "aria-label": "WhatsApp", children: _jsxs("svg", { viewBox: "0 0 24 24", width: "21", height: "21", fill: "white", xmlns: "http://www.w3.org/2000/svg", style: { width: '21px', height: '21px', color: '#ffffff' }, children: [_jsx("path", { d: "M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" }), _jsx("path", { d: "M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.832-1.438A9.96 9.96 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2zm0 18a8 8 0 01-4.29-1.24l-.31-.18-2.87.85.78-2.83-.21-.33A8 8 0 1112 20z" })] }) })), _jsx(motion.button, { type: "button", onClick: handleNewChat, "aria-label": t('clearChat'), title: t('clearChat'), style: {
                                                background: 'rgba(255, 255, 255, 0.15)',
                                                border: 'none',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: '32px',
                                                height: '32px',
                                                borderRadius: '10px',
                                                padding: 0,
                                            }, whileHover: { background: 'rgba(255, 255, 255, 0.25)' }, whileTap: { scale: 0.95 }, children: _jsx(Trash2, { style: { width: '16px', height: '16px', color: '#ffffff' } }) }), sidebarConfig.enabled && window.innerWidth >= 768 && (_jsx(motion.button, { type: "button", onClick: () => setWidgetState(isSidebar ? 'expanded' : 'sidebar'), "aria-label": isSidebar ? t('expandChat') : t('collapseSidebar'), title: isSidebar ? t('expandChat') : t('collapseSidebar'), style: {
                                                background: 'rgba(255, 255, 255, 0.15)',
                                                border: 'none',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: '32px',
                                                height: '32px',
                                                borderRadius: '10px',
                                                padding: 0,
                                            }, whileHover: { background: 'rgba(255, 255, 255, 0.25)' }, whileTap: { scale: 0.95 }, children: isSidebar ? (_jsx(Maximize2, { style: { width: '16px', height: '16px', color: '#ffffff' } })) : sidebarConfig.side === 'left' ? (_jsx(PanelLeftClose, { style: { width: '16px', height: '16px', color: '#ffffff' } })) : (_jsx(PanelRightClose, { style: { width: '16px', height: '16px', color: '#ffffff' } })) })), _jsx(motion.button, { type: "button", onClick: () => minimizeWidget(), whileHover: { background: 'rgba(255, 255, 255, 0.25)' }, whileTap: { scale: 0.95 }, style: {
                                                width: '32px',
                                                height: '32px',
                                                borderRadius: '10px',
                                                background: 'rgba(255, 255, 255, 0.15)',
                                                border: 'none',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                padding: 0,
                                                flexShrink: 0,
                                            }, "aria-label": "Close chat", children: _jsx(X, { style: { width: '16px', height: '16px', color: '#ffffff' } }) })] })] }), _jsxs("div", { ref: messagesContainerRef, onScroll: handleScroll, className: wac('overflow-y-auto'), style: {
                                flex: '1 1 0%',
                                minHeight: 0,
                                padding: '24px 32px 32px',
                                touchAction: 'pan-y',
                                overflowX: 'hidden',
                            }, children: [conversationState === 'empty' && (_jsx("div", { children: _jsxs(motion.div, { className: wac('flex'), style: { gap: '12px' }, initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.4 }, children: [_jsx(motion.div, { className: wac('rounded-full flex items-center justify-center flex-shrink-0'), style: {
                                                    width: '32px',
                                                    height: '32px',
                                                    background: 'var(--waa-primary, #6366F1)',
                                                }, animate: {
                                                    boxShadow: [
                                                        '0 0 0 0 rgba(99, 102, 241, 0.4)',
                                                        '0 0 0 8px rgba(99, 102, 241, 0)',
                                                        '0 0 0 0 rgba(99, 102, 241, 0)',
                                                    ],
                                                }, transition: {
                                                    duration: 2,
                                                    repeat: Infinity,
                                                    repeatType: 'loop',
                                                }, children: _jsx("span", { className: wac('text-white font-semibold'), style: { fontSize: '12px' }, children: "AI" }) }), _jsxs("div", { className: wac('flex-1'), children: [_jsx("p", { style: {
                                                            fontSize: '13px',
                                                            lineHeight: '21.125px',
                                                            color: 'var(--waa-gray-700)',
                                                            marginBottom: '8px',
                                                        }, children: getWelcomeMessage() }), _jsx("p", { style: {
                                                            fontSize: '13px',
                                                            lineHeight: '21.125px',
                                                            color: 'var(--waa-gray-700)',
                                                            marginBottom: '8px',
                                                        }, children: "Ask me things like \"Show me bestselling products\" or \"What are your shipping options?\"" }), _jsx("p", { style: {
                                                            fontSize: '13px',
                                                            lineHeight: '20px',
                                                            color: 'var(--waa-gray-500)',
                                                            marginTop: '4px',
                                                            marginBottom: '8px',
                                                            cursor: 'pointer',
                                                        }, onClick: () => imageInputRef.current?.click(), children: t('uploadImageCTA') }), _jsxs("div", { style: {
                                                            fontSize: '12px',
                                                            color: 'var(--waa-gray-400)',
                                                        }, children: [getBotName(), " \u2022 ", introTimestamp] })] })] }) })), conversationState === 'conversation' && (_jsxs("div", { className: wac('space-y-4'), children: [hasMoreHistory && (_jsx("div", { className: wac('flex justify-center'), children: _jsx("button", { onClick: loadMoreHistory, disabled: isLoadingHistory, className: wac('px-4 py-2 text-sm rounded-lg'), style: {
                                                    background: 'var(--waa-primary-50)',
                                                    color: 'var(--waa-primary)',
                                                    border: '1px solid var(--waa-primary-100)',
                                                    cursor: isLoadingHistory ? 'not-allowed' : 'pointer',
                                                    opacity: isLoadingHistory ? 0.6 : 1,
                                                }, children: isLoadingHistory
                                                    ? t('loading') || 'Loading...'
                                                    : t('loadOlderMessages') || 'Load older messages' }) })), messages.length === 0 && !loading && (_jsxs("div", { className: wac('flex gap-3'), children: [_jsx("div", { className: wac('w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0'), style: { background: 'var(--waa-primary-50)' }, children: _jsx("span", { className: wac('text-xs font-semibold'), style: { color: 'var(--waa-primary)' }, children: "AI" }) }), _jsxs("div", { className: wac('px-4 py-3 rounded-2xl text-[15px] leading-relaxed'), style: {
                                                        maxWidth: '75%',
                                                        background: 'var(--waa-chat-bot-bg)',
                                                        color: 'var(--waa-chat-bot-text)',
                                                        borderBottomLeftRadius: isRTL ? '16px' : '4px',
                                                        borderBottomRightRadius: isRTL ? '4px' : '16px',
                                                    }, children: [getWelcomeMessage(), _jsx("p", { style: {
                                                                fontSize: '13px',
                                                                lineHeight: '20px',
                                                                color: 'var(--waa-gray-500)',
                                                                marginTop: '8px',
                                                                cursor: 'pointer',
                                                            }, onClick: () => imageInputRef.current?.click(), children: t('uploadImageCTA') })] })] })), messages
                                            .filter((m) => m.content || m.products?.length)
                                            .map((message) => (_jsx(motion.div, { initial: { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.22, ease: 'easeOut' }, children: _jsx(ChatMessage, { message: message, onAddToCart: handleAddToCart, sessionId: sessionId || undefined }) }, message.id))), loading && _jsx(TypingIndicator, {}), whatsappConfig.enabled &&
                                            whatsappConfig.phone &&
                                            shouldShowEscalation(messages, whatsappConfig.escalationTurns) &&
                                            !escalationDismissed && (_jsxs("div", { style: {
                                                margin: '12px 0',
                                                padding: '12px 16px',
                                                background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
                                                borderRadius: '12px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '12px',
                                                color: 'white',
                                                fontSize: '13px',
                                                position: 'relative',
                                            }, children: [_jsxs("svg", { viewBox: "0 0 24 24", width: "24", height: "24", fill: "white", style: { flexShrink: 0 }, children: [_jsx("path", { d: "M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" }), _jsx("path", { d: "M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.832-1.438A9.96 9.96 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2zm0 18a8 8 0 01-4.29-1.24l-.31-.18-2.87.85.78-2.83-.21-.33A8 8 0 1112 20z" })] }), _jsxs("div", { style: { flex: 1 }, children: [_jsx("div", { style: { fontWeight: 600, marginBottom: '2px' }, children: t('whatsappEscalation') }), _jsxs("button", { onClick: () => {
                                                                const url = buildWhatsAppUrl(whatsappConfig.phone, messages, whatsappConfig.includeContext);
                                                                window.open(url, '_blank');
                                                            }, style: {
                                                                background: 'rgba(255,255,255,0.2)',
                                                                border: '1px solid rgba(255,255,255,0.4)',
                                                                borderRadius: '6px',
                                                                color: 'white',
                                                                padding: '4px 12px',
                                                                cursor: 'pointer',
                                                                fontSize: '12px',
                                                                fontWeight: 600,
                                                                marginTop: '4px',
                                                            }, children: [t('whatsappHandoff'), " \u2192"] })] }), _jsx("button", { onClick: () => {
                                                        setEscalationDismissed(true);
                                                        try {
                                                            sessionStorage.setItem('waa-whatsapp-escalation-dismissed', '1');
                                                        }
                                                        catch { }
                                                    }, style: {
                                                        position: 'absolute',
                                                        top: '4px',
                                                        right: '8px',
                                                        background: 'none',
                                                        border: 'none',
                                                        color: 'rgba(255,255,255,0.7)',
                                                        cursor: 'pointer',
                                                        fontSize: '16px',
                                                        padding: '2px',
                                                    }, "aria-label": t('whatsappDismiss'), children: "\u00D7" })] })), _jsx("div", { ref: messagesEndRef })] })), conversationState === 'products' && (_jsxs("div", { className: wac('space-y-4'), children: [_jsx(ChatMessage, { message: {
                                                id: '1',
                                                type: 'bot',
                                                content: 'Perfect! I found some excellent options for you. Swipe through to explore all products:',
                                                timestamp: introTimestamp,
                                            } }), _jsx("div", { className: wac('mt-4'), style: { minWidth: 0, maxWidth: '100%', overflow: 'hidden' }, children: _jsx(ProductCarousel, { products: products, onViewProduct: handleViewProduct, onAddToCart: handleAddToCart, sessionId: sessionId || undefined, showCarouselArrows: window.wooAIChatConfig?.appearance?.showCarouselArrows === true, carouselArrowSvg: window.wooAIChatConfig?.appearance?.carouselArrowSvg }) }), _jsx(ChatMessage, { message: {
                                                id: '2',
                                                type: 'bot',
                                                content: 'Need more information about any product? Or would you like to see different options?',
                                                timestamp: introTimestamp,
                                            } }), loading && _jsx(TypingIndicator, {})] })), conversationState === 'playbook' && (_jsxs("div", { className: wac('space-y-4'), children: [_jsx(ChatMessage, { message: {
                                                id: '1',
                                                type: 'system',
                                                content: '🎯 Starting product finder guide...',
                                            } }), _jsx(PlaybookStep, { step: {
                                                number: 1,
                                                total: 4,
                                                question: t('playbookQuestion'),
                                                options: [
                                                    { id: '1', label: `💼 ${t('workBusiness')}`, emoji: '💼' },
                                                    { id: '2', label: `🎮 ${t('gaming')}`, emoji: '🎮' },
                                                    { id: '3', label: `🎓 ${t('studyLearning')}`, emoji: '🎓' },
                                                    { id: '4', label: `🎨 ${t('creativeWork')}`, emoji: '🎨' },
                                                ],
                                            } }), loading && _jsx(TypingIndicator, {})] }))] }), _jsx(TopicsBar, { onTopicClick: (_topic, message) => {
                                // Expand widget and trigger conversation (preserve sidebar state)
                                setWidgetState((prev) => (prev === 'sidebar' ? 'sidebar' : 'expanded'));
                                setConversationState('conversation');
                                // Send the topic message
                                sendMessage(message, {
                                    currentPage: typeof window !== 'undefined' ? window.location.pathname : 'N/A',
                                    pageContext: window.wooAIChatConfig?.pageContext || undefined,
                                });
                            } }), _jsxs("div", { className: wac('border-t'), style: {
                                flexShrink: 0,
                                padding: '10px 16px 10px',
                                borderColor: 'var(--waa-gray-200)',
                            }, children: [pendingImage && (_jsx("div", { style: { padding: '0 0 8px', display: 'flex', justifyContent: 'flex-start' }, children: _jsxs("div", { style: { position: 'relative', display: 'inline-block' }, children: [_jsx("img", { src: pendingImage.preview, alt: "Upload", style: {
                                                    maxHeight: '80px',
                                                    borderRadius: '8px',
                                                    border: '1px solid var(--waa-gray-200)',
                                                } }), _jsx("button", { onClick: () => setPendingImage(null), style: {
                                                    position: 'absolute',
                                                    top: '-6px',
                                                    right: '-6px',
                                                    width: '20px',
                                                    height: '20px',
                                                    borderRadius: '50%',
                                                    background: 'var(--waa-gray-700)',
                                                    color: '#fff',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    fontSize: '12px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }, children: "\u00D7" })] }) })), _jsxs("div", { className: wac('relative'), onDrop: handleDrop, onDragOver: handleDragOver, onDragLeave: handleDragLeave, style: dragOver
                                        ? { outline: '2px dashed var(--waa-primary, #4f46e5)', borderRadius: '8px' }
                                        : undefined, children: [_jsx("input", { ref: chatInputRef, type: "text", value: input, onChange: (e) => setInput(e.target.value), onKeyDown: (e) => e.key === 'Enter' && handleSend(), onPaste: handlePaste, placeholder: t('typePlaceholder'), className: wac('w-full border focus:outline-none focus:ring-1 focus:ring-offset-0'), style: {
                                                ...sharedInputStyles,
                                                height: '40px',
                                                paddingInlineStart: '12px',
                                                paddingInlineEnd: '80px',
                                                borderRadius: '8px',
                                                borderColor: 'var(--waa-gray-300)',
                                                // Keep >=16px so iOS does not auto-zoom on focus — the
                                                // expanded-chat composer is the input Nastya focuses when
                                                // the keyboard opens; sub-16px left the page stuck zoomed
                                                // after close (#363).
                                                fontSize: '16px',
                                                direction: dir,
                                            } }), _jsx("button", { type: "button", onClick: () => {
                                                imageInputRef.current?.click();
                                                if (imagePulsing) {
                                                    setImagePulsing(false);
                                                    try {
                                                        sessionStorage.setItem('waa-image-pulse-shown', '1');
                                                    }
                                                    catch { }
                                                }
                                            }, style: {
                                                position: 'absolute',
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                [isRTL ? 'left' : 'right']: '40px',
                                                background: 'none',
                                                border: 'none',
                                                cursor: 'pointer',
                                                padding: '4px',
                                                display: 'flex',
                                                flexDirection: 'row',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: 'auto',
                                                height: '32px',
                                                gap: '4px',
                                            }, "aria-label": "Upload image", title: t('searchByPhoto'), children: _jsx("span", { className: imagePulsing ? 'waa-image-pulse' : '', style: {
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    width: '24px',
                                                    height: '18px',
                                                }, children: _jsxs("svg", { width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", stroke: pendingImage ? 'var(--waa-primary)' : 'var(--waa-gray-400)', strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [_jsx("rect", { x: "3", y: "3", width: "18", height: "18", rx: "2", ry: "2" }), _jsx("circle", { cx: "8.5", cy: "8.5", r: "1.5" }), _jsx("polyline", { points: "21 15 16 10 5 21" })] }) }) }), _jsx("input", { ref: imageInputRef, type: "file", accept: "image/*", onChange: handleImageSelect, style: { display: 'none' } }), _jsx(motion.button, { type: "button", onClick: () => handleSend(), disabled: !input.trim() || loading, whileHover: { scale: 1.15 }, whileTap: { scale: 0.9, rotate: 15 }, className: wac('absolute flex items-center justify-center transition-opacity disabled:opacity-30 disabled:cursor-not-allowed'), style: isRTL
                                                ? {
                                                    left: '8px',
                                                    top: '50%',
                                                    marginTop: '-12px',
                                                    width: '24px',
                                                    height: '24px',
                                                }
                                                : {
                                                    right: '8px',
                                                    top: '50%',
                                                    marginTop: '-12px',
                                                    width: '24px',
                                                    height: '24px',
                                                }, "aria-label": "Send message", children: _jsx(Send, { className: wac('w-4 h-4'), style: { color: 'var(--waa-gray-500)' } }) })] })] }), _jsx(AnimatePresence, { children: cartNotification.show && (_jsxs(motion.div, { initial: { opacity: 0, y: -20, scale: 0.95 }, animate: { opacity: 1, y: 0, scale: 1 }, exit: { opacity: 0, y: -20, scale: 0.95 }, transition: { type: 'spring', damping: 25, stiffness: 300 }, className: wac('absolute left-1/2 -translate-x-1/2 flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg'), style: {
                                    top: '70px',
                                    background: '#10B981',
                                    color: '#ffffff',
                                    zIndex: 10000,
                                    maxWidth: 'calc(100% - 32px)',
                                }, children: [_jsx("div", { className: wac('flex items-center justify-center w-6 h-6 rounded-full'), style: { background: 'rgba(255,255,255,0.2)' }, children: _jsx(Check, { className: wac('w-4 h-4') }) }), _jsxs("span", { className: wac('text-sm font-medium whitespace-nowrap'), children: [t('addedToCart'), " \u2713"] })] })) })] }) })] }));
}
